import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, Plus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { tierAiBundle, tierDisplayName, type Tier } from "@/lib/tier";
import type { Workspace } from "@/hooks/useWorkspace";
import { BuyCreditsDialog } from "./BuyCreditsDialog";
import { CreditHistoryPanel } from "./CreditHistoryPanel";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

type CreditStats = {
  tier: Tier;
  month_start: string;
  account_workspace_count: number;
  monthly_credits_cap: number | null;
  monthly_credits_used: number;
  topup_credits_balance: number;
  byok_active: boolean;
};

const fmtInt = (n: number) => n.toLocaleString();

type Props = { workspace: Workspace };

/**
 * AI credits meter for the current account. Mounted next to AiProviderSettingsCard.
 * Shows monthly credit allowance, top-up balance, and a Buy credits CTA.
 *
 * CRITICAL: Displays credits only — no USD, no raw token counts. Internal
 * cost basis must never leak into the UI.
 */
export const AiUsageMeterCard = ({ workspace }: Props) => {
  const tier = workspace.account_tier ?? null;
  const bundle = tierAiBundle(tier);
  const [buyOpen, setBuyOpen] = useState(false);
  const [explainerOpen, setExplainerOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const cardRef = useRef<HTMLDivElement | null>(null);

  // Deep-link: /settings?buy_credits=1 opens the buy-credits dialog (e.g.
  // when a composer error toast offers a "Buy credits" action).
  useEffect(() => {
    if (searchParams.get("buy_credits") === "1") {
      setBuyOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("buy_credits");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Deep-link: /settings?focus=ai-credits scrolls this card into view, then
  // clears the param so a back-nav doesn't re-trigger the scroll.
  useEffect(() => {
    if (searchParams.get("focus") !== "ai-credits") return;
    const t = setTimeout(() => {
      cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      const next = new URLSearchParams(searchParams);
      next.delete("focus");
      setSearchParams(next, { replace: true });
    }, 150);
    return () => clearTimeout(t);
  }, [searchParams, setSearchParams]);

  const q = useQuery({
    queryKey: ["account-ai-usage", workspace.id],
    queryFn: async (): Promise<CreditStats> => {
      const { data, error } = await supabase.functions.invoke("account-ai-usage", {
        body: { workspace_id: workspace.id },
      });
      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      return data as CreditStats;
    },
    enabled: bundle !== "none",
  });

  if (bundle === "none") return null;

  if (q.isLoading) {
    return (
      <Card className="shadow-soft">
        <CardHeader><CardTitle>AI credits this month</CardTitle></CardHeader>
        <CardContent><Skeleton className="h-12 w-full" /></CardContent>
      </Card>
    );
  }

  if (q.isError || !q.data) {
    return (
      <Card className="shadow-soft">
        <CardHeader><CardTitle>AI credits this month</CardTitle></CardHeader>
        <CardContent className="text-xs text-muted-foreground">
          Couldn't load usage right now.
        </CardContent>
      </Card>
    );
  }

  const data = q.data;
  const cap = data.monthly_credits_cap;
  const used = data.monthly_credits_used;
  const remaining = cap !== null ? Math.max(0, cap - used) : null;
  const pct = cap !== null && cap > 0 ? Math.min(100, (used / cap) * 100) : 0;
  const danger = pct >= 80;
  const isByok = data.byok_active;
  const isByokTier = data.tier === "elite" || data.tier === "agency";

  // Status pill mirrors the threshold notifications so the user sees the
  // same state the email/notification told them about.
  let statusLabel: string | null = null;
  let statusVariant: "default" | "secondary" | "destructive" | "outline" = "secondary";
  if (cap !== null && cap > 0) {
    if (pct >= 100) {
      statusLabel = "Cap reached";
      statusVariant = "destructive";
    } else if (pct >= 80) {
      statusLabel = "80% used";
      statusVariant = "outline";
    } else {
      statusLabel = "All good";
      statusVariant = "secondary";
    }
  }

  return (
    <>
      <Card id="ai-credits" ref={cardRef} className="shadow-soft scroll-mt-20">
        <CardHeader>
          <CardTitle className="flex items-center justify-between gap-2">
            <span>AI credits this month</span>
            <div className="flex items-center gap-2">
              {statusLabel && (
                <Badge variant={statusVariant} className="text-[10px]">
                  {statusLabel}
                </Badge>
              )}
              <span className="text-xs font-normal text-muted-foreground">
                {tierDisplayName(data.tier)}
              </span>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          {cap !== null ? (
            <div className="space-y-1.5">
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Monthly allowance</span>
                <span>
                  {fmtInt(used)} / {fmtInt(cap)} used
                  {remaining !== null && (
                    <span className="text-muted-foreground"> · {fmtInt(remaining)} left</span>
                  )}
                </span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                <div
                  className={`h-full ${danger ? "bg-destructive" : "bg-primary"}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <p className="text-[11px] text-muted-foreground">
                Resets on the 1st. After that, top-up credits below are used.
              </p>
            </div>
          ) : isByokTier ? (
            <p className="text-xs text-muted-foreground">
              Your plan uses your own OpenRouter key for premium writing.
              {isByok ? " Key is active." : " Add one below to enable premium writing."}
              {" "}Top-up credits below cover non-BYOK paths (planning).
            </p>
          ) : null}

          <div className="flex items-center justify-between rounded-md border border-border/60 bg-muted/30 px-3 py-2">
            <div>
              <div className="text-xs text-muted-foreground">Top-up balance</div>
              <div className="text-base font-semibold">
                {fmtInt(data.topup_credits_balance)} credits
              </div>
              <div className="text-[10px] text-muted-foreground">Rolls over · never expires</div>
            </div>
            <Button size="sm" onClick={() => setBuyOpen(true)}>
              <Plus className="h-3.5 w-3.5 mr-1" />
              Buy credits
            </Button>
          </div>

          <Collapsible open={explainerOpen} onOpenChange={setExplainerOpen}>
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-between -ml-2"
              >
                <span className="text-xs">How credits work</span>
                <ChevronDown
                  className={`h-3 w-3 transition-transform ${explainerOpen ? "rotate-180" : ""}`}
                />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-1">
              <ul className="space-y-1 pl-4 text-[11px] text-muted-foreground list-disc">
                <li>Monthly credits reset on the 1st and don't roll over.</li>
                <li>Top-up credits and any credits gifted by Excerptus roll over and never expire.</li>
                <li>Composer drafts and planning all draw from the same pool.</li>
              </ul>
            </CollapsibleContent>
          </Collapsible>

          <Button asChild variant="link" size="sm" className="h-auto p-0 text-xs">
            <a href="/settings/credits">View full credits dashboard →</a>
          </Button>

          <CreditHistoryPanel />
        </CardContent>
      </Card>

      <BuyCreditsDialog
        open={buyOpen}
        onOpenChange={setBuyOpen}
        workspaceId={workspace.id}
      />
    </>
  );
};
